import { Link } from "react-router-dom";
import { Phone, Home as HomeIcon, ArrowRight, AlertTriangle } from "lucide-react";
import { SITE, NAV } from "../lib/site";
import { POSTS } from "../lib/blog";
import SEO from "../components/SEO";
import { breadcrumbJsonLd } from "../lib/seo";

export default function NotFound() {
    const popularPosts = POSTS.slice(0, 4);

    return (
        <div data-testid="not-found-page" className="bg-white">
            <SEO
                title="Page Not Found — Mike's Garage Door Repair Loveland"
                description="Page not found. Browse our garage door repair services or call 970-541-4025 for fast same-day repair in Loveland, Colorado and all of Northern CO."
                path="/404"
                noindex
                jsonLd={[
                    breadcrumbJsonLd([
                        { name: "Home", href: "/" },
                        { name: "Page not found", href: "/404" },
                    ]),
                ]}
            />

            <section className="bg-cream border-b border-zinc-200 py-16 lg:py-24">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="inline-flex h-16 w-16 items-center justify-center bg-brand-yellow rounded-sm shadow">
                        <AlertTriangle className="h-8 w-8 text-black" strokeWidth={2.5} />
                    </div>
                    <p className="mt-6 font-display text-brand-yellow-ink text-base tracking-[0.3em] font-bold uppercase">
                        Error 404
                    </p>
                    <h1 className="mt-2 font-display text-4xl sm:text-5xl lg:text-6xl text-zinc-900 uppercase leading-[1.05]">
                        Looks Like This Page
                        <span className="block text-brand-yellow-ink">Got Off The Track.</span>
                    </h1>
                    <p className="mt-5 max-w-2xl mx-auto text-zinc-700 text-base sm:text-lg leading-relaxed">
                        The page you're looking for doesn't exist or was moved. While
                        you're here — if you've got a stuck door, broken spring or a
                        dead opener, give us a call and we'll diagnose it in under two
                        minutes.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                        <a
                            href={SITE.phoneTel}
                            data-testid="not-found-call-btn"
                            className="inline-flex items-center justify-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-black font-display tracking-wider text-base px-6 py-3 rounded-sm transition-colors uppercase shadow"
                        >
                            <Phone className="h-4 w-4" strokeWidth={2.5} />
                            Click to Call {SITE.phoneDisplay}
                        </a>
                        <Link
                            to="/"
                            data-testid="not-found-home-btn"
                            className="inline-flex items-center justify-center gap-2 border-2 border-zinc-300 hover:border-brand-yellow hover:text-brand-yellow-ink text-zinc-800 font-display tracking-wider text-base px-6 py-3 rounded-sm transition-colors uppercase"
                        >
                            <HomeIcon className="h-4 w-4" />
                            Back Home
                        </Link>
                    </div>
                </div>
            </section>

            <section className="bg-white py-14 lg:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 lg:gap-14">
                    <div>
                        <p className="text-brand-yellow-ink text-xs tracking-[0.25em] font-bold uppercase">
                            Try these instead
                        </p>
                        <h2 className="mt-3 font-display text-2xl sm:text-3xl text-zinc-900 uppercase leading-tight">
                            Popular Pages
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {NAV.filter((n) => n.href !== "/").map((n) => (
                                <li key={n.href}>
                                    <Link
                                        to={n.href}
                                        data-testid={`not-found-link-${n.testId}`}
                                        className="group flex items-center justify-between gap-3 bg-cream border border-zinc-200 hover:border-brand-yellow rounded-sm px-5 py-4 transition-colors"
                                    >
                                        <span className="font-display text-base sm:text-lg text-zinc-900 uppercase tracking-wide">
                                            {n.label}
                                        </span>
                                        <ArrowRight className="h-4 w-4 text-zinc-400 group-hover:text-brand-yellow-ink transition-colors" />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div>
                        <p className="text-brand-yellow-ink text-xs tracking-[0.25em] font-bold uppercase">
                            From the blog
                        </p>
                        <h2 className="mt-3 font-display text-2xl sm:text-3xl text-zinc-900 uppercase leading-tight">
                            Recent Articles
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {popularPosts.map((p) => (
                                <li key={p.slug}>
                                    <Link
                                        to={`/blog/${p.slug}`}
                                        data-testid={`not-found-post-${p.slug}`}
                                        className="group flex items-start gap-4 bg-cream border border-zinc-200 hover:border-brand-yellow rounded-sm p-4 transition-colors"
                                    >
                                        <img
                                            src={p.cover}
                                            srcSet={`${p.cover.replace(".webp", "-800.webp")} 800w, ${p.cover} 1280w`}
                                            sizes="(max-width: 768px) 96px, 120px"
                                            alt={p.title}
                                            loading="lazy"
                                            decoding="async"
                                            width="120"
                                            height="80"
                                            className="w-24 h-16 object-cover rounded-sm flex-shrink-0"
                                        />
                                        <span className="font-semibold text-zinc-900 text-sm sm:text-base leading-snug group-hover:text-brand-yellow-ink transition-colors">
                                            {p.title}
                                        </span>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}
